import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ThemeToggle from "./ThemeToggle";
import { useAuth } from "../context/AuthContext";
import "./Navbar.css";

const Navbar = ({ className = "" }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, getUsername } = useAuth();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navClasses = [
    "navbar",
    isScrolled ? "navbar--scrolled" : "",
    isMobileMenuOpen ? "navbar--mobile-open" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/products", label: "Products" },
    { path: "/lab", label: "The Lab" },
    {
      path: "https://github.com/AxiomLabsSolutions",
      label: "GitHub",
      external: true,
    },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <nav className={navClasses}>
      <div className="navbar__container container">
        <Link
          to="/"
          className="navbar__logo"
          aria-label="AxiomLabs Solutions Home"
        >
          <svg
            className="navbar__logo-svg"
            width="140"
            height="40"
            viewBox="0 0 420 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g transform="translate(20, 30)">
              <rect
                x="0"
                y="0"
                width="28"
                height="28"
                rx="2"
                className="navbar__logo-icon"
              />
              <rect
                x="34"
                y="0"
                width="28"
                height="28"
                rx="2"
                className="navbar__logo-icon"
              />
              <rect
                x="0"
                y="34"
                width="28"
                height="28"
                rx="2"
                className="navbar__logo-icon"
              />
              <rect
                x="34"
                y="34"
                width="28"
                height="28"
                rx="2"
                className="navbar__logo-icon"
              />
            </g>
            <text
              x="110"
              y="70"
              fontFamily="'IBM Plex Mono', monospace"
              fontSize="48"
              fontWeight="700"
              fill="currentColor"
              letterSpacing="-1.5px"
              className="navbar__logo-name"
            >
              AxiomLabs
            </text>
            <text
              x="110"
              y="97"
              fontFamily="'IBM Plex Mono', monospace"
              fontSize="18"
              fontWeight="500"
              fill="currentColor"
              letterSpacing="2px"
              className="navbar__logo-sub"
            >
              SOLUTIONS
            </text>
          </svg>
        </Link>

        <ul className="navbar__links navbar__links--desktop">
          {navLinks.map((link) => (
            <li key={link.path} className="navbar__link-item">
              {link.external ? (
                <a
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="navbar__link"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  to={link.path}
                  className={`navbar__link ${location.pathname === link.path ? "navbar__link--active" : ""}`}
                >
                  {link.label}
                </Link>
              )}
            </li>
          ))}
        </ul>

        <div className="navbar__actions">
          <ThemeToggle />
          {user ? (
            <button
              className="navbar__user"
              onClick={() => navigate("/auth")}
              title={user.email}
            >
              <span className="navbar__user-avatar">
                {getUsername(user.email).charAt(0).toUpperCase()}
              </span>
              <span className="navbar__user-name">
                {getUsername(user.email)}
              </span>
            </button>
          ) : (
            <Link to="/auth" className="navbar__signin">
              Sign In
            </Link>
          )}
        </div>

        <button
          className="navbar__hamburger"
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation menu"
          aria-expanded={isMobileMenuOpen}
        >
          <span className="navbar__hamburger-line" />
          <span className="navbar__hamburger-line" />
          <span className="navbar__hamburger-line" />
        </button>

        <ul
          className={`navbar__links navbar__links--mobile ${isMobileMenuOpen ? "navbar__links--mobile-open" : ""}`}
        >
          {navLinks.map((link) => (
            <li key={link.path} className="navbar__link-item">
              {link.external ? (
                <a
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="navbar__link"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  to={link.path}
                  className={`navbar__link ${location.pathname === link.path ? "navbar__link--active" : ""}`}
                >
                  {link.label}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
